import { FC } from "react";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminClaimsApi } from "../../http/claims/api";
import { getClaimStatuses } from "../../http/claims/types";
import { formatCurrency, formatDate } from "../../types/utils";
import HistoryDialog from "../ui/HistoryDialog";
import VersionDialog from "../ui/VersionDialog";

type ClaimHistoryDialogProps = {
  itemToShowVersionsId: string;
  itemVersionToShowVersions: number;
  onClose: () => void;
};

const ClaimHistoryDialog: FC<ClaimHistoryDialogProps> = ({
  itemToShowVersionsId,
  itemVersionToShowVersions,
  onClose,
}) => {
  const i18n = useI18n();
  const claimsApi = useAdminClaimsApi();

  const statuses = getClaimStatuses(i18n.t);

  return (
    <HistoryDialog
      title={i18n.t.common.versions}
      entityId={itemToShowVersionsId}
      lastVersion={itemVersionToShowVersions}
      getHistory={claimsApi.getAdminClaimHistory}
      getEntityVersionHook={claimsApi.getAdminClaimVersion}
      onClose={onClose}
      renderForm={(headers, dataVersion) => (
        <>
          <VersionDialog.Table headers={headers}>
            {dataVersion.current?.number != dataVersion.next.number && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.number}
                  value={dataVersion.current.number}
                  edited={dataVersion.next.number}
                />
              </tr>
            )}
            {dataVersion.current?.status != dataVersion.next.status && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.state}
                  value={statuses.find((x) => x.value == dataVersion.current.status)?.label}
                  edited={statuses.find((x) => x.value == dataVersion.next.status)?.label}
                />
              </tr>
            )}
            {dataVersion.current?.openingDate != dataVersion.next.openingDate && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.openingDate}
                  value={dataVersion.current && formatDate(dataVersion.current?.openingDate, i18n.language)}
                  edited={formatDate(dataVersion.next.openingDate, i18n.language)}
                />
              </tr>
            )}
            {dataVersion.current?.closingDate != dataVersion.next.closingDate && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.closingDate}
                  value={
                    dataVersion.current?.closingDate && formatDate(dataVersion.current?.closingDate, i18n.language)
                  }
                  edited={dataVersion.next.closingDate && formatDate(dataVersion.next.closingDate, i18n.language)}
                />
              </tr>
            )}
            {dataVersion.current?.incidentDate != dataVersion.next.incidentDate && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.claims.incidentDate}
                  value={dataVersion.current && formatDate(dataVersion.current?.incidentDate, i18n.language)}
                  edited={formatDate(dataVersion.next.incidentDate, i18n.language)}
                />
              </tr>
            )}
            {(dataVersion.current?.car?.id ?? null) !== (dataVersion.next.car?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.cars.singular}
                  value={
                    dataVersion.current?.car?.currentPlateNumber ||
                    dataVersion.current?.car?.vehicleIdentificationNumber
                  }
                  edited={dataVersion.next.car?.currentPlateNumber || dataVersion.next.car?.vehicleIdentificationNumber}
                />
              </tr>
            )}
            {(dataVersion.current?.subsidiary?.id ?? null) !== (dataVersion.next.subsidiary?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.subsidiaries.singular}
                  value={dataVersion.current?.subsidiary?.name}
                  edited={dataVersion.next.subsidiary?.name}
                />
              </tr>
            )}
            {(dataVersion.current?.type?.id ?? null) !== (dataVersion.next.type?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.type}
                  value={dataVersion.current?.type?.name}
                  edited={dataVersion.next.type?.name}
                />
              </tr>
            )}
            {dataVersion.current?.description != dataVersion.next.description && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.description}
                  value={dataVersion.current.description}
                  edited={dataVersion.next.description}
                />
              </tr>
            )}
            {dataVersion.current?.damageValueGross != dataVersion.next.damageValueGross && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.claims.damageValue}
                  value={
                    dataVersion.current.damageValueGross &&
                    `${formatCurrency(dataVersion.current?.damageValueGross, i18n.language)}`
                  }
                  edited={
                    dataVersion.next.damageValueGross &&
                    formatCurrency(dataVersion.next.damageValueGross, i18n.language)
                  }
                />
              </tr>
            )}
            {dataVersion.current?.damageValueNet != dataVersion.next.damageValueNet && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.claims.damageValue}
                  value={
                    dataVersion.current.damageValueNet &&
                    `${formatCurrency(dataVersion.current?.damageValueNet, i18n.language)}`
                  }
                  edited={
                    dataVersion.next.damageValueNet && formatCurrency(dataVersion.next.damageValueNet, i18n.language)
                  }
                />
              </tr>
            )}
            {dataVersion.current?.settledAmountGross != dataVersion.next.settledAmountGross && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.claims.damageValue}
                  value={
                    dataVersion.current.settledAmountGross &&
                    `${formatCurrency(dataVersion.current?.settledAmountGross, i18n.language)}`
                  }
                  edited={
                    dataVersion.next.settledAmountGross &&
                    formatCurrency(dataVersion.next.settledAmountGross, i18n.language)
                  }
                />
              </tr>
            )}
            {dataVersion.current?.settledAmountNet != dataVersion.next.settledAmountNet && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.claims.damageValue}
                  value={
                    dataVersion.current.settledAmountNet &&
                    `${formatCurrency(dataVersion.current?.settledAmountNet, i18n.language)}`
                  }
                  edited={
                    dataVersion.next.settledAmountNet &&
                    formatCurrency(dataVersion.next.settledAmountNet, i18n.language)
                  }
                />
              </tr>
            )}
          </VersionDialog.Table>
        </>
      )}
    />
  );
};

export default ClaimHistoryDialog;
